/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from './http';
import { getPlatform } from './platform';

/**
 * 埋点事件数据接口
 */
export interface EventData {
  uuid: string;           // 用户设备唯一ID
  event: string;          // 事件类型
  type: number;           // 事件分类
  record: string;         // 页面路径path
  ext: string;            // 页面域名
  platform?: string;
  ua?: string;
}

/**
 * 埋点事件输入数据接口（不包含uuid，因为uuid是公共参数）
 */
export interface EventInputData {
  event: string;          // 事件类型
  type: number;           // 事件分类
  record: string;         // 页面路径path
  ext: string;            // 页面域名
  platform?: string;
  ua?: string;
}

/**
 * Stats配置接口
 */
export interface StatsConfig {
  baseURL?: string;
  timeout?: number;
  retryCount?: number;
  enableBatch?: boolean;    // 是否启用批量上报
  batchSize?: number;       // 批量上报大小
  batchTimeout?: number;    // 批量上报超时时间(ms)
  enableCache?: boolean;    // 是否启用离线缓存
}

/**
 * 批量事件数据接口
 */
interface BatchEventData {
  events: EventData[];
  timestamp: number;
}

/**
 * 埋点数据上报类（单例模式）
 * 遵循单一职责原则，专门负责数据埋点和上报
 * 默认使用单个事件上报模式，发送格式：{"uuid":"xxx","event":"xxx","type":2,"record":"xxx","ext":"xxx"}
 * 注意：init()时会自动发送页面曝光事件，开发环境下可能因React严格模式执行两次
 */
export class Stats {
  private static instance: Stats | null = null;
  private httpClient: HttpClient;
  private config: Required<StatsConfig>;
  private eventQueue: EventData[] = [];
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly STORAGE_KEY = 'stats_cache';
  private readonly UUID_STORAGE_KEY = 'BS_UUID';
  private readonly API_ENDPOINT = '/v1/common/events';
  private isInitialized = false;
  private userUUID = '';

  private constructor(config: StatsConfig = {}) {
    // 默认配置
    this.config = {
      baseURL: 'https://8ws1romljc.execute-api.us-west-2.amazonaws.com',
      timeout: 5000,
      retryCount: 3,
      enableBatch: false,  // 改为false，直接发送单个事件对象
      batchSize: 10,
      batchTimeout: 5000,
      enableCache: true,
      ...config
    };

    // 创建专用的HTTP客户端
    this.httpClient = new HttpClient({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      retryCount: this.config.retryCount,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * 获取Stats单例实例
   */
  public static getInstance(config?: StatsConfig): Stats {
    if (!Stats.instance) {
      Stats.instance = new Stats(config);
    }
    return Stats.instance;
  }

  /**
   * 初始化Stats实例
   * 生成用户唯一ID，处理缓存事件，设置页面卸载处理器
   */
  public async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 生成或获取用户唯一ID
      this.userUUID = this.generateOrGetUUID();
      
      // 初始化时处理缓存的事件
      await this.processCachedEvents();

      // 页面卸载时处理剩余事件
      this.setupUnloadHandler();

      // 自动发送页面曝光事件
      await this.sendPageExposureEvent();

      this.isInitialized = true;
      this.logSuccess('Stats初始化完成', { uuid: this.userUUID });
    } catch (error) {
      this.handleError('init', error);
    }
  }

  /**
   * 上报事件数据
   * 默认直接发送单个事件对象：{"uuid":"xxx","event":"xxx","type":2,"record":"xxx","ext":"xxx"}
   * @param eventData 事件数据（不需要传uuid，会自动添加）
   */
  public async event(eventData: EventInputData): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      // 自动添加uuid作为公共参数
      const completeEventData: EventData = {
        uuid: this.userUUID,
        ...eventData
      };

      // 数据验证
      this.validateEventData(completeEventData);

      if (this.config.enableBatch) {
        // 批量上报模式
        this.addToQueue(completeEventData);
      } else {
        // 立即上报模式
        await this.sendSingleEvent(completeEventData);
      }
    } catch (error) {
      this.handleError('event', error);
    }
  }

  /**
   * 立即发送所有队列中的事件
   */
  public async flush(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    try {
      await this.sendBatchEvents();
    } catch (error) {
      this.handleError('flush', error);
    }
  }

  /**
   * 发送页面曝光事件
   */
  private async sendPageExposureEvent(): Promise<void> {
    try {
      await this.event({
        event: 'ip_url_record',
        type: 2,
        record: this.getCurrentPath(),
        ext: this.getCurrentDomain(),
        platform: getPlatform(),
        ua: navigator.userAgent,
      });
      this.logSuccess('页面曝光事件发送成功', {
        path: this.getCurrentPath(),
        domain: this.getCurrentDomain()
      });
    } catch (error) {
      this.handleError('sendPageExposureEvent', error);
    }
  }

  /**
   * 验证事件数据
   */
  private validateEventData(eventData: EventData): void {
    if (!eventData.uuid) {
      throw new Error('UUID不能为空');
    }
    if (!eventData.event) {
      throw new Error('事件类型不能为空');
    }
    if (typeof eventData.type !== 'number') {
      throw new Error('事件分类必须是数字');
    }
    if (!eventData.record) {
      throw new Error('记录内容不能为空');
    }
    if (!eventData.ext) {
      throw new Error('扩展信息不能为空');
    }
  }

  /**
   * 添加事件到队列
   */
  private addToQueue(eventData: EventData): void {
    this.eventQueue.push(eventData);

    // 检查是否达到批量大小
    if (this.eventQueue.length >= this.config.batchSize) {
      this.sendBatchEvents();
      return;
    }

    // 设置批量超时
    this.setBatchTimer();
  }

  /**
   * 设置批量上报定时器
   */
  private setBatchTimer(): void {
    if (this.batchTimer) {
      return;
    }

    this.batchTimer = setTimeout(() => {
      this.sendBatchEvents();
    }, this.config.batchTimeout);
  }

  /**
   * 清除批量上报定时器
   */
  private clearBatchTimer(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
  }

  /**
   * 发送单个事件
   */
  private async sendSingleEvent(eventData: EventData): Promise<void> {
    try {
      await this.httpClient.post(this.API_ENDPOINT, eventData);
      this.logSuccess('单个事件上报成功', eventData);
    } catch (error) {
      // 如果启用缓存，将失败的事件保存到本地
      if (this.config.enableCache) {
        this.cacheEvent(eventData);
      }
      throw error;
    }
  }

  /**
   * 发送批量事件
   */
  private async sendBatchEvents(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];
    this.clearBatchTimer();

    try {
      // 批量发送事件
      const batchData: BatchEventData = {
        events: eventsToSend,
        timestamp: Date.now()
      };

      await this.httpClient.post(this.API_ENDPOINT, batchData);
      this.logSuccess('批量事件上报成功', batchData);
    } catch (error) {
      // 如果启用缓存，将失败的事件保存到本地
      if (this.config.enableCache) {
        eventsToSend.forEach(event => this.cacheEvent(event));
      }
      throw error;
    }
  }

  /**
   * 缓存事件到本地存储
   */
  private cacheEvent(eventData: EventData): void {
    try {
      const cachedEvents = this.getCachedEvents();
      cachedEvents.push(eventData);
      
      // 限制缓存大小，避免占用过多存储空间
      const maxCacheSize = 100;
      if (cachedEvents.length > maxCacheSize) {
        cachedEvents.splice(0, cachedEvents.length - maxCacheSize);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cachedEvents));
    } catch (error) {
      // 静默处理缓存失败
    }
  }

  /**
   * 获取缓存的事件
   */
  private getCachedEvents(): EventData[] {
    try {
      const cached = localStorage.getItem(this.STORAGE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      // 静默处理读取失败
      return [];
    }
  }

  /**
   * 处理缓存的事件
   */
  private async processCachedEvents(): Promise<void> {
    if (!this.config.enableCache) {
      return;
    }

    const cachedEvents = this.getCachedEvents();
    if (cachedEvents.length === 0) {
      return;
    }

    try {
      // 重新上报缓存的事件
      for (const event of cachedEvents) {
        if (this.config.enableBatch) {
          this.addToQueue(event);
        } else {
          await this.sendSingleEvent(event);
        }
      }

      // 清除缓存
      localStorage.removeItem(this.STORAGE_KEY);
      this.logSuccess('处理缓存事件完成', { count: cachedEvents.length });
    } catch (error) {
      // 静默处理缓存事件失败
    }
  }

  /**
   * 生成或获取UUID
   * 遵循KISS原则，使用简单的UUID生成方法
   * 优化：优先读取本地缓存中的'uuid'键值，如果存在就将其赋值给'BS_UUID'
   */
  private generateOrGetUUID(): string {
    // 优先检查是否已存储BS_UUID
    const existingBsUUID = localStorage.getItem(this.UUID_STORAGE_KEY);
    if (existingBsUUID) {
      return existingBsUUID;
    }

    // 检查是否存在广告SDK使用的'uuid'键值
    const existingUUID = localStorage.getItem('uuid');
    if (existingUUID) {
      // 如果存在'uuid'，将其值赋给'BS_UUID'
      localStorage.setItem(this.UUID_STORAGE_KEY, existingUUID);
      this.logSuccess('从uuid键迁移UUID到BS_UUID', { uuid: existingUUID });
      return existingUUID;
    }

    // 生成新的UUID
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });

    // 保存到本地存储
    localStorage.setItem(this.UUID_STORAGE_KEY, uuid);
    return uuid;
  }

  /**
   * 获取当前用户UUID
   */
  public getUserUUID(): string {
    return this.userUUID;
  }

  /**
   * 获取当前页面路径
   */
  private getCurrentPath(): string {
    return window.location.pathname;
  }

  /**
   * 获取当前页面域名
   */
  private getCurrentDomain(): string {
    return window.location.hostname;
  }

  /**
   * 设置页面卸载处理器
   */
  private setupUnloadHandler(): void {
    // 页面卸载时发送剩余事件
    window.addEventListener('beforeunload', () => {
      if (this.eventQueue.length > 0) {
        // 使用sendBeacon API进行可靠的数据发送
        this.sendBeaconEvents();
      }
    });

    // 页面隐藏时也发送事件（移动端兼容）
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.eventQueue.length > 0) {
        this.sendBeaconEvents();
      }
    });
  }

  /**
   * 使用Beacon API发送事件
   */
  private sendBeaconEvents(): void {
    if (this.eventQueue.length === 0) {
      return;
    }

    try {
      const batchData = {
        events: this.eventQueue,
        timestamp: Date.now()
      };

      const url = `${this.config.baseURL}${this.API_ENDPOINT}`;
      const data = JSON.stringify(batchData);

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, data);
      } else {
        // 降级处理：缓存到本地
        this.eventQueue.forEach(event => this.cacheEvent(event));
      }

      this.eventQueue = [];
      this.clearBatchTimer();
    } catch (error) {
      // 静默处理Beacon发送失败
    }
  }

  /**
   * 记录成功日志
   */
  private logSuccess(_message: string, _data: any): void {
    // 移除控制台打印，保持静默
  }

  /**
   * 错误处理
   */
  private handleError(_method: string, _error: any): void {
    // 静默处理错误
    
    // 可以在这里添加错误上报逻辑
    // 例如发送到错误监控服务
  }

  /**
   * 更新配置
   */
  public updateConfig(newConfig: Partial<StatsConfig>): void {
    Object.assign(this.config, newConfig);
    
    // 更新HTTP客户端配置
    this.httpClient.setDefaultConfig({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout
    });
  }

  /**
   * 销毁实例
   */
  public destroy(): void {
    this.clearBatchTimer();
    
    // 发送剩余事件
    if (this.eventQueue.length > 0) {
      this.sendBeaconEvents();
    }

    this.isInitialized = false;
  }

  /**
   * 重置单例实例（主要用于测试）
   */
  public static resetInstance(): void {
    if (Stats.instance) {
      Stats.instance.destroy();
      Stats.instance = null;
    }
  }
}

// 创建默认的Stats单例实例
export const stats = Stats.getInstance(); 